import {
  Alert,
  Box,
  Card,
  CardContent,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MicroAppQueryResult, MicroAppView } from "../../api/client.ts";
import { formatNumber, humanize } from "../../utils/format.ts";
import ScrollTable from "../table/ScrollTable.tsx";

/**
 * First-party rendering of a manifest's declarative `views` — no app-authored
 * markup runs here. Shared by the Vayken host page and the TV share page.
 */

const SERIES_COLORS = [
  "var(--mui-palette-primary-main)",
  "var(--mui-palette-success-main)",
  "var(--mui-palette-warning-main)",
  "var(--mui-palette-info-main)",
  "var(--mui-palette-error-main)",
  "var(--mui-palette-secondary-main)",
];

export const WIDTHS: Record<MicroAppView["width"], string> = {
  full: "1 1 100%",
  half: "1 1 calc(50% - 12px)",
  third: "1 1 calc(33.333% - 16px)",
};

function toRecords(result: MicroAppQueryResult): Record<string, unknown>[] {
  const columns = result.columns ?? [];
  return (result.rows ?? []).map((row) =>
    Object.fromEntries(columns.map((c, i) => [c, row[i]])),
  );
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return formatNumber(value);
  return String(value);
}

function aggregate(view: MicroAppView, result: MicroAppQueryResult): string {
  const rows = result.rows ?? [];
  if (view.agg === "count") return formatNumber(rows.length);

  const columns = result.columns ?? [];
  const idx = view.column ? columns.indexOf(view.column) : -1;
  if (idx < 0) return "—";

  const values = rows
    .map((r) => r[idx])
    .filter((v): v is number => typeof v === "number");
  if (view.agg === "first") return formatCell(rows[0]?.[idx]);
  if (!values.length) return "—";

  switch (view.agg) {
    case "sum":
      return formatNumber(values.reduce((a, b) => a + b, 0));
    case "avg":
      // One decimal at most.
      return formatNumber(Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10);
    case "min":
      return formatNumber(Math.min(...values));
    case "max":
      return formatNumber(Math.max(...values));
    default:
      return "—";
  }
}

export function ViewPanel({ view, result }: { view: MicroAppView; result?: MicroAppQueryResult }) {
  if (!result) {
    return <Alert severity="warning">No data returned for query “{view.query}”.</Alert>;
  }
  if (result.error) {
    return (
      <Alert severity={result.status === 403 ? "info" : "error"}>
        {result.status === 403
          ? "You don’t have access to the data behind this panel."
          : result.error}
      </Alert>
    );
  }

  if (view.type === "stat") {
    return (
      <Box>
        <Typography variant="h3" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
          {aggregate(view, result)}
          {view.unit ? (
            <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 0.5 }}>
              {view.unit}
            </Typography>
          ) : null}
        </Typography>
      </Box>
    );
  }

  if (view.type === "table") {
    const columns = view.columns?.length ? view.columns : (result.columns ?? []);
    const indexes = columns.map((c) => (result.columns ?? []).indexOf(c));
    const rows = result.rows ?? [];
    if (!rows.length) {
      return <Typography color="text.secondary">No rows.</Typography>;
    }
    return (
      <ScrollTable containerSx={{ maxHeight: 420 }} stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c}>{humanize(c)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.slice(0, 500).map((row, i) => (
            <TableRow key={i} hover>
              {indexes.map((idx, j) => (
                <TableCell key={j}>{formatCell(idx >= 0 ? row[idx] : null)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </ScrollTable>
    );
  }

  // line | bar
  const records = toRecords(result);
  const series = view.y ?? [];
  const ChartRoot = view.type === "line" ? LineChart : BarChart;
  return (
    <Box sx={{ height: 300 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ChartRoot data={records}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--mui-palette-divider)" />
          <XAxis dataKey={view.x ?? ""} tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <RechartsTooltip />
          {series.length > 1 ? <Legend /> : null}
          {series.map((key, i) =>
            view.type === "line" ? (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                dot={false}
              />
            ) : (
              <Bar key={key} dataKey={key} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
            ),
          )}
        </ChartRoot>
      </ResponsiveContainer>
    </Box>
  );
}

/** Every view of a manifest, laid out by its `width`. */
export function DeclarativeViews({
  views,
  data,
}: {
  views: MicroAppView[];
  data: Record<string, MicroAppQueryResult>;
}) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "stretch" }}>
      {views.map((view, i) => (
        <Card key={i} variant="outlined" sx={{ flex: WIDTHS[view.width], minWidth: 260 }}>
          <CardContent>
            {view.title ? (
              <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {view.title}
              </Typography>
            ) : null}
            <ViewPanel view={view} result={data[view.query]} />
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
